'use client';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInputWithMask } from '@/components/form/ControlledInputWithMask/ControlledInputWithMask';
import { ControlledCheckbox } from '@/components/form/ControlledCheckbox/ControlledCheckbox';
import { ControlledInput } from '@/components/form/ControlledInput/ControlledInput';
import { SuccessPopup } from '@/components/common/SuccessPopup/SuccessPopup';
import { PhoneLink } from '@/components/ui/PhoneLink/PhoneLink';
import { TextLink } from '@/components/ui/TextLink/TextLink';
import { Button } from '@/components';
import { SvgContactsWhite, SvgEmail } from '@/components/icons';
import { personal } from '@/constants/docs';
import { submitForm } from '@/api/leads/leads';
import { callbackFormSchema, CallbackFormSchema } from '@/components/callbackForm/callbackFormSchema';
import { MedicalUnit } from '@/api/medicalUnits/medicalUnits.types';
import s from './ContactUs.module.scss';

interface Props {
  aboutData: MedicalUnit | null;
  closeModal: () => void;
}

export const ContactUs = ({ aboutData, closeModal }: Props) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const form = useForm<CallbackFormSchema>({
      resolver: zodResolver(callbackFormSchema),
      defaultValues: {
        name: '',
        phone: '',
        privacy_consent: false,
      },
    });

    const submit = async (data: CallbackFormSchema) => {
      await submitForm({ ...data, phone: `+${data.phone}` });
      setIsSuccess(true);
    };

    if (isSuccess) return <SuccessPopup onClose={closeModal} />;

    return (
      <div className={s.contactUs}>
        <div className={s.contactUs__info}>
          <h2>Свяжитесь с нами</h2>
          <div className={s.contactUs__contacts}>
            <div className={s.contactUs__contactLink}>
              <SvgEmail />
              <a href={`mailto:${aboutData?.email}`} className={s.email}>{aboutData?.email}</a>
            </div>

            <div className={s.contactUs__contactLink}>
              <SvgContactsWhite />
              <PhoneLink className={s.phone} phone={aboutData?.main_phone} codeIsFour />
            </div>
          </div>
        </div>
        <FormProvider {...form}>
          <div className={s.contactUs__form}>
            <div className={s.contactUs__fields}>
              <ControlledInputWithMask
                fieldName={'phone'}
                maskOptions={[{ mask: '+{7}(000)000-00-00' }]}
                placeholder={'+7 (___) ___ -__-__'}
              />
              <ControlledInput fieldName={'name'} placeholder={'Ваше имя'} />
            </div>
            <ControlledCheckbox fieldName={'privacy_consent'}>
              <>Я принимаю <TextLink text={'Политику конфиденциальности и обработку персональных данных'} href={personal} target={'_blank'} /></>
            </ControlledCheckbox>
            <div className={s.contactUs__buttonWrapper}>
              <Button
                onClick={form.handleSubmit(submit)}
                disabled={!!Object.keys(form.formState.errors).length}
                name="Заказать обратный звонок"
                theme="footer"
                size="block"
              />
              <p className={s.contactUs__caption}>
                Вам будет совершен обратный звонок, дождитесь ответа оператора.
                Нажимая на кнопку Заказать обратный звонок, Вы соглашаетесь с обработкой своих персональных данных
                Ставроссмед
              </p>
            </div>
          </div>
        </FormProvider>
      </div>
    );
  }
;
